"use client";

/**
 * Isolated light/dark appearance for **published** public VirtualSpace
 * pages (`/share/[token]` and `/explore/[token]`).
 *
 * Intentionally does NOT use `next-themes` — nested ThemeProvider is a
 * no-op in next-themes@0.4 (child renders Fragment only), and mutating
 * `<html class="dark">` would fight the signed-in dashboard preference.
 *
 * Instead we scope design tokens + page backdrop on `.vs-public-light` /
 * `.vs-public-dark` (see `globals.css`) and gate Tailwind conditionals
 * with the `vs-dark:` variant where needed.
 */

import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { ComponentProps } from "react";
import { Moon, Sun } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import LogoMark from "@/components/virtualspace/logo-mark";
import { ScopedThemePortalProvider } from "@/components/scoped-theme-portal";
import { VIRTUALSPACE_PUBLIC_THEME_SHELL_CLASS } from "@/lib/virtualspace/virtualspace-public-viewport";

export const VIRTUALSPACE_PUBLIC_THEME_STORAGE_KEY =
  "virtualspace-public-appearance";

type VsPublicAppearance = "light" | "dark";

type Ctx = {
  appearance: VsPublicAppearance;
  setAppearance: (v: VsPublicAppearance) => void;
};

const VirtualSpacePublicAppearanceContext = createContext<Ctx | null>(null);

export function useVirtualSpacePublicAppearance(): Ctx {
  const v = useContext(VirtualSpacePublicAppearanceContext);
  if (!v) {
    throw new Error(
      "useVirtualSpacePublicAppearance must be used under VirtualSpacePublicThemeShell",
    );
  }
  return v;
}

export function VirtualSpacePublicThemeShell({
  children,
  forcedAppearance,
}: {
  children: React.ReactNode;
  /** Full-bleed viewer chrome needs stable contrast without changing page preferences. */
  forcedAppearance?: VsPublicAppearance;
}) {
  const [savedAppearance, setAppearanceState] = useState<VsPublicAppearance>("dark");
  const appearance = forcedAppearance ?? savedAppearance;

  useLayoutEffect(() => {
    if (forcedAppearance) return;
    try {
      const raw = localStorage.getItem(VIRTUALSPACE_PUBLIC_THEME_STORAGE_KEY);
      if (raw === "light" || raw === "dark") {
        setAppearanceState(raw);
      }
    } catch {
      /* private mode / quota */
    }
  }, [forcedAppearance]);

  const setAppearance = useCallback((next: VsPublicAppearance) => {
    if (forcedAppearance) return;
    setAppearanceState(next);
    try {
      localStorage.setItem(VIRTUALSPACE_PUBLIC_THEME_STORAGE_KEY, next);
    } catch {
      /* ignore */
    }
  }, [forcedAppearance]);

  const ctx = useMemo(
    () => ({ appearance, setAppearance }),
    [appearance, setAppearance],
  );

  return (
    <VirtualSpacePublicAppearanceContext.Provider value={ctx}>
      <div
        suppressHydrationWarning
        className={cn(
          VIRTUALSPACE_PUBLIC_THEME_SHELL_CLASS,
          appearance === "dark"
            ? "vs-public-dark vs-canvas-bg vs-grid-bg"
            : "vs-public-light bg-background",
        )}
        data-vs-theme-scope={appearance}
        style={{
          colorScheme: appearance === "dark" ? "dark" : "light",
        }}
      >
        <ScopedThemePortalProvider>{children}</ScopedThemePortalProvider>
      </div>
    </VirtualSpacePublicAppearanceContext.Provider>
  );
}

/**
 * Wordmark lockups track the isolated VirtualSpace appearance instead
 * of `<html class="dark">` so operator global dark mode does not invert
 * the logo when a recipient picks light mode.
 */
export function VirtualSpacePublicLogoMark(
  props: Omit<ComponentProps<typeof LogoMark>, "forceTheme">,
) {
  const { appearance } = useVirtualSpacePublicAppearance();
  return (
    <LogoMark
      {...props}
      forceTheme={appearance === "dark" ? "dark" : "light"}
    />
  );
}

export function VirtualSpacePublicThemeToggle({
  className,
}: {
  className?: string;
}) {
  const { appearance, setAppearance } = useVirtualSpacePublicAppearance();
  const isDark = appearance === "dark";
  return (
    <Button
      type="button"
      variant="outline"
      size="icon"
      // Tap target: 44×44 on phones (WCAG / iOS HIG min), 36×36 on sm+
      // where mouse precision makes the smaller chrome more elegant
      // alongside the operator title.
      className={cn(
        "h-11 w-11 sm:h-9 sm:w-9 shrink-0 vs-btn-outline !rounded-[var(--radius-sm)] !px-0",
        className,
      )}
      onClick={() => setAppearance(isDark ? "light" : "dark")}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
    >
      {isDark ? (
        <Sun className="h-4 w-4" aria-hidden />
      ) : (
        <Moon className="h-4 w-4" aria-hidden />
      )}
    </Button>
  );
}
